import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { FlaskConical } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { Button } from '../components/atoms/Button/Button';
import { Grid } from '../components/atoms/Grid/Grid';
import { EmptyState } from '../components/atoms/EmptyState';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { ConfirmDialog } from '../components/ui/ConfirmDialog/ConfirmDialog';
import { Dialog } from '../components/ui/Dialog/Dialog';
import { Text } from '../components/atoms/Typography/Text';
import { Alert } from '../components/atoms/Alert/Alert';
import { DosingScheduleCard } from '../components/dosing/DosingScheduleCard';
import { DosingScheduleForm } from '../components/dosing/DosingScheduleForm';
import {
  listDosingSchedules,
  deleteDosingSchedule,
  resumeDosingSchedule,
  type DosingSchedule,
} from '../services/dosingScheduleService';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { DosingLogSection } from '../components/dosing/DosingLogSection';
import { DosingLogForm, type DosingLogFormMode } from '../components/dosing/DosingLogForm';
import { DosingPauseForm } from '../components/dosing/DosingPauseForm';
import { KhSupplySection } from '../components/dosing/KhSupplySection';
import { DosingSafetyCard } from '../components/dosing/DosingSafetyCard';
import { listDosingLogs, deleteDosingLog, type DosingLog } from '../services/dosingLogService';
import { invalidateAllTasks } from '../hooks/useTasks';

export function DosingPage() {
  const { t } = useTranslation();
  const { aquariumId = '' } = useParams<{ aquariumId: string }>();
  const queryClient = useQueryClient();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<DosingSchedule | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DosingSchedule | null>(null);
  const [logFormMode, setLogFormMode] = useState<DosingLogFormMode | null>(null);
  const [deleteLogTarget, setDeleteLogTarget] = useState<DosingLog | null>(null);
  const [pauseTarget, setPauseTarget] = useState<DosingSchedule | null>(null);
  /** A dose suggested by a voice note (?edit=<plan>&dose=<amount>, UC-4) — filled into the editor, saved only by the user. */
  const [suggestedDose, setSuggestedDose] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const schedulesQuery = useQuery({
    queryKey: ['dosingSchedules', aquariumId],
    queryFn: () => listDosingSchedules(aquariumId),
    enabled: aquariumId.length > 0,
  });

  // Reading the log also catches up due dosing-pump doses on the server.
  const logsQuery = useQuery({
    queryKey: ['dosingLogs', aquariumId],
    queryFn: () => listDosingLogs(aquariumId),
    enabled: aquariumId.length > 0,
  });

  function invalidateLogs(): void {
    void queryClient.invalidateQueries({ queryKey: ['dosingLogs', aquariumId] });
    // Manual plans drive their due doses in Aufgaben: plan changes refresh the tasks too.
    invalidateAllTasks(queryClient);
  }

  const deleteLogMutation = useMutation({
    mutationFn: (id: string) => deleteDosingLog(aquariumId, id),
    onSuccess: () => {
      invalidateLogs();
      setDeleteLogTarget(null);
    },
  });

  // "Fortsetzen": doses resume from now on; the paused period is not filled in.
  const resumeMutation = useMutation({
    mutationFn: (id: string) => resumeDosingSchedule(aquariumId, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dosingSchedules', aquariumId] });
      invalidateLogs();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteDosingSchedule(aquariumId, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dosingSchedules', aquariumId] });
      invalidateLogs();
      setDeleteTarget(null);
    },
  });

  const schedules = schedulesQuery.data ?? [];

  // Deep link from a voice note's dosing-plan change: open that plan's editor once, then drop the parameters.
  const editParam = searchParams.get('edit');
  useEffect(() => {
    if (!editParam || !schedulesQuery.data) return;
    const target = schedulesQuery.data.find((s) => s.id === editParam);
    const dose = Number(searchParams.get('dose'));
    if (target) {
      setEditingSchedule(target);
      setSuggestedDose(Number.isFinite(dose) && dose > 0 ? dose : null);
      setIsFormOpen(true);
    }
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('edit');
        next.delete('dose');
        return next;
      },
      { replace: true },
    );
  }, [editParam, schedulesQuery.data, searchParams, setSearchParams]);

  function handleOpenCreate(): void {
    setEditingSchedule(null);
    setIsFormOpen(true);
  }

  function handleOpenEdit(schedule: DosingSchedule): void {
    setSuggestedDose(null);
    setEditingSchedule(schedule);
    setIsFormOpen(true);
  }

  function handleCloseForm(): void {
    setIsFormOpen(false);
    setEditingSchedule(null);
    setSuggestedDose(null);
  }

  function handleFormSuccess(): void {
    void queryClient.invalidateQueries({ queryKey: ['dosingSchedules', aquariumId] });
    invalidateLogs();
    setIsFormOpen(false);
    setEditingSchedule(null);
    setSuggestedDose(null);
  }

  function handleRequestDelete(schedule: DosingSchedule): void {
    setDeleteTarget(schedule);
  }

  function handleCancelDelete(): void {
    setDeleteTarget(null);
  }

  function handleConfirmDelete(): void {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget.id);
    }
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent>
          <PageHeader
            title={t('dosing.title')}
            description={t('dosing.subtitle')}
            actions={
              <Button type="button" variant="primary" onClick={handleOpenCreate}>
                {t('dosing.schedules.addButton')}
              </Button>
            }
          />

          <Section title={t('dosing.schedules.title')}>
            {schedulesQuery.isLoading ? (
              <LiveRegion>
                <Text variant="muted">{t('dosing.schedules.loading')}</Text>
              </LiveRegion>
            ) : schedulesQuery.isError ? (
              <LoadErrorState
                title={t('dosing.schedules.loadErrorTitle')}
                message={t('dosing.schedules.loadErrorMessage')}
                onRetry={() => void schedulesQuery.refetch()}
              />
            ) : schedules.length === 0 ? (
              <EmptyState
                icon={<FlaskConical size={48} strokeWidth={1.5} />}
                headline={t('dosing.schedules.emptyHeadline')}
                description={t('dosing.schedules.empty')}
                actionLabel={t('dosing.schedules.addButton')}
                actionVariant="secondary"
                onAction={handleOpenCreate}
              />
            ) : (
              <Grid minItemWidth="md" gap="4">
                {schedules.map(schedule => (
                  <DosingScheduleCard
                    key={schedule.id}
                    schedule={schedule}
                    onEdit={handleOpenEdit}
                    onDelete={handleRequestDelete}
                    onLogNow={(s) => setLogFormMode({ kind: 'create', schedule: s })}
                    onPause={setPauseTarget}
                    onResume={(s) => resumeMutation.mutate(s.id)}
                  />
                ))}
              </Grid>
            )}
          </Section>

          {resumeMutation.isError && (
            <Alert tone="danger" onDismiss={() => resumeMutation.reset()} dismissLabel={t('dosing.pause.dismiss')} data-testid="dosing-resume-error">
              {t('dosing.pause.resumeError')}
            </Alert>
          )}

          <KhSupplySection aquariumId={aquariumId} />

          <DosingSafetyCard />

          <DosingLogSection
            logsQuery={logsQuery}
            onLogDose={() => setLogFormMode({ kind: 'create' })}
            onEditLog={(log) => setLogFormMode({ kind: 'edit', log })}
            onDeleteLog={setDeleteLogTarget}
          />

          <Dialog
            isOpen={logFormMode !== null}
            onClose={() => setLogFormMode(null)}
            title={logFormMode?.kind === 'edit' ? t('dosing.logForm.editTitle') : t('dosing.logForm.createTitle')}
            data-testid="dosing-log-form-dialog"
          >
            {logFormMode && (
              <DosingLogForm
                aquariumId={aquariumId}
                mode={logFormMode}
                onSuccess={() => {
                  invalidateLogs();
                  setLogFormMode(null);
                }}
                onCancel={() => setLogFormMode(null)}
              />
            )}
          </Dialog>

          <Dialog
            isOpen={pauseTarget !== null}
            onClose={() => setPauseTarget(null)}
            title={t('dosing.pause.title', { product: pauseTarget?.product_name ?? '' })}
            data-testid="dosing-pause-dialog"
          >
            {pauseTarget && (
              <DosingPauseForm
                aquariumId={aquariumId}
                schedule={pauseTarget}
                onSuccess={() => {
                  void queryClient.invalidateQueries({ queryKey: ['dosingSchedules', aquariumId] });
                  invalidateLogs();
                  setPauseTarget(null);
                }}
                onCancel={() => setPauseTarget(null)}
              />
            )}
          </Dialog>

          <ConfirmDialog
            isOpen={deleteLogTarget !== null}
            title={t('dosing.logList.deleteConfirm.title')}
            message={
              deleteLogTarget?.source === 'pump'
                ? t('dosing.logList.deleteConfirm.messagePump')
                : t('dosing.logList.deleteConfirm.message')
            }
            confirmLabel={t('dosing.logList.deleteConfirm.confirm')}
            cancelLabel={t('dosing.deleteConfirm.cancel')}
            onConfirm={() => {
              if (deleteLogTarget) deleteLogMutation.mutate(deleteLogTarget.id);
            }}
            onCancel={() => {
              deleteLogMutation.reset();
              setDeleteLogTarget(null);
            }}
            isConfirming={deleteLogMutation.isPending}
          >
            {deleteLogMutation.isError && (
              <Alert tone="danger" data-testid="dosing-log-delete-error">
                {t('dosing.logList.deleteError')}
              </Alert>
            )}
          </ConfirmDialog>

          <Dialog
            isOpen={isFormOpen}
            onClose={handleCloseForm}
            title={editingSchedule ? t('dosing.form.editTitle') : t('dosing.form.addTitle')}
            data-testid="dosing-form-dialog"
          >
            <DosingScheduleForm
              aquariumId={aquariumId}
              schedule={editingSchedule ?? undefined}
              suggestedDoseAmount={editingSchedule ? (suggestedDose ?? undefined) : undefined}
              onSuccess={handleFormSuccess}
              onCancel={handleCloseForm}
            />
          </Dialog>

          <ConfirmDialog
            isOpen={deleteTarget !== null}
            title={t('dosing.deleteConfirm.title')}
            message={t('dosing.deleteConfirm.message')}
            confirmLabel={t('dosing.deleteConfirm.confirm')}
            cancelLabel={t('dosing.deleteConfirm.cancel')}
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
          />
        </PageContent>
      }
    />
  );
}

export default DosingPage;
